import _ from "lodash";
import React, { useState, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { Checkbox, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";

import ACLStore from "lib/aclStore";
import { getStyleStrippedText } from "Utils";

import classes from "./TreeNode.scss";

const TreeNode = React.memo(props => {
  const {
    value,
    label,
    subText,
    code,
    handleCheckboxClick,
    depth: initialDepth,
    isListType,
    useSubText,
    isNodeSelected,
    depthOffset,
    showPopupForSubFields,
    updatePopoverRef,
    togglePopover,
    searchText,
    testId,
    isLeaf,
    getLearningGoalsTagCountTooltip,
    shouldUpdateActiveQuestionOnClick,
    allowParentTagging,
  } = props;

  const [isChecked, setIsChecked] = useState(false);

  const depth = initialDepth > 1 ? initialDepth + depthOffset : initialDepth;

  const nodeContainerClasses = classNames({
    [classes.nodeContainer]: true,
    [classes.leafNodeContainer]: isLeaf,
    [classes.nodeBorderContainer]: !isListType,
  });

  const nodeInnerContainerClasses = classNames({
    [classes.nodeInnerContainer]: true,
    [classes.listInnerContainer]: isListType,
    [classes.rightEndLabelContainerClass]:
      isLeaf &&
      !_.isNil(
        getLearningGoalsTagCountTooltip?.(
          value,
          shouldUpdateActiveQuestionOnClick
        )
      ),
  });

  const labelClasses = classNames({
    [classes.defaultLabel]: !_.isEmpty(searchText),
    ["heading-5"]: depth == 1 && !isListType,
    ["heading-6"]: depth == 2,
    ["text-body-1"]: depth == 3,
    ["text-body-2"]: depth > 3 || isListType,
  });

  const listTypeClasses = classNames({
    [classes.labelDepth3]: depth == 3 && !allowParentTagging && !isLeaf,
    [classes.labelDepth4]: depth == 4 && !allowParentTagging && !isLeaf,
    [classes.listItemWithIcon]: showPopupForSubFields && isLeaf,
  });

  const codeClasses = classNames(
    "text-textSubtle",
    labelClasses,
    classes.codeLabel
  );

  const checkBoxWrapperClasses = classNames({
    [classes.checkboxWrapper]: true,
    [classes.checkboxWrapperDepth2Indented]: allowParentTagging && depth == 2,
    [classes.checkboxWrapperDepth3Indented]: allowParentTagging && depth == 3,
    [classes.checkboxWrapperDepth4Indented]: allowParentTagging && depth == 4,
    [classes.checkboxWrapperDepth5Indented]: allowParentTagging && depth == 5,
    [classes.checkboxWrapperDepth4]: !allowParentTagging && depth == 4,
    [classes.checkboxWrapperDepth5]: !allowParentTagging && depth == 5,
  });

  useLayoutEffect(() => {
    if (isNodeSelected) {
      setIsChecked(isNodeSelected(value));
    }
  }, []);

  const onClickCheckbox = () => {
    setIsChecked(!isChecked);
    handleCheckboxClick(value);
  };

  const renderLabel = () => {
    const nodeLabel = useSubText && subText ? subText : label;

    return (
      <span className={listTypeClasses}>
        <span
          className={labelClasses}
          dangerouslySetInnerHTML={{
            __html: getStyleStrippedText({
              text: nodeLabel,
              type: "SANITIZE_DANGER_HTML_PARAGRAPH",
            }),
          }}
        />
        {code ? (
          <span
            className={codeClasses}
            dangerouslySetInnerHTML={{
              __html: ` (${getStyleStrippedText({
                text: code,
                type: "SNS_EDITOR",
              })})`,
            }}
          />
        ) : null}
        {ACLStore.can("FeatureFlag:EnableDescriptionInSNS") &&
          !useSubText &&
          subText && (
            <span style={{ marginLeft: "4px", verticalAlign: "middle" }}>
              <Tooltip
                tooltip={subText}
                overlayInnerStyle={{ maxWidth: "344px" }}
              >
                <InformationOutlined
                  size="xxx-small"
                  variant="subtle"
                  style={{ position: "relative", top: "2px" }}
                />
              </Tooltip>
            </span>
          )}
        {showPopupForSubFields && isLeaf ? (
          <IconButton
            variant="plain"
            icon={<InformationOutlined />}
            size="x-small"
            onClick={() => togglePopover(value)}
            ref={node => updatePopoverRef(node, value)}
          />
        ) : null}
      </span>
    );
  };

  return (
    <div className={nodeContainerClasses}>
      <div className={nodeInnerContainerClasses}>
        {isLeaf || allowParentTagging ? (
          <Checkbox
            isChecked={isChecked}
            onChange={onClickCheckbox}
            checkboxWrapperClassName={checkBoxWrapperClasses}
            shouldStopPropagation={true}
            testId={`${testId}-item-${value}`}
          >
            {renderLabel()}
          </Checkbox>
        ) : (
          renderLabel()
        )}
        {isLeaf
          ? getLearningGoalsTagCountTooltip?.(
              value,
              shouldUpdateActiveQuestionOnClick
            )
          : null}
      </div>
    </div>
  );
});

TreeNode.displayName = "TreeNode";

TreeNode.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  subText: PropTypes.string,
  code: PropTypes.string,
  handleCheckboxClick: PropTypes.func,
  depth: PropTypes.number,
  useSubText: PropTypes.bool,
  isNodeSelected: PropTypes.func,
  showPopupForSubFields: PropTypes.bool,
  updatePopoverRef: PropTypes.func,
  togglePopover: PropTypes.func,
  isLeaf: PropTypes.bool,
};

TreeNode.defaultProps = {
  subText: "",
  code: "",
  useSubText: false,
  isNodeSelected: PropTypes.func,
  showPopupForSubFields: false,
  updatePopoverRef: () => {},
  togglePopover: () => {},
  depthOffset: 0,
};

export default TreeNode;
